import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { COMPLIANCE_DOCUMENT_KINDS, type ComplianceDocumentKind, type ProductCompliance } from "@osmix/contracts/compliance";
import { api, type Product } from "@/api";
import { productKeys } from "@/api/hooks/use-products";
import { complianceJournalKey, ComplianceReviewPanel, ruDay } from "@/components/compliance-review";
import { complianceSummaryKey } from "@/components/compliance-strip";
import { Select } from "@/components/ui/select";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";

/** Почему консоль не даёт опубликовать карточку; null - можно. */
export const complianceBlockText = (compliance: ProductCompliance | undefined, t: (key: string) => string) => {
  if (!compliance?.blocksPublication) return null;
  if (compliance.state === "expired") return t("compliance.detail.expired");
  if (compliance.state === "rejected") return t("compliance.detail.rejected");
  if (compliance.state === "pending") return t("compliance.detail.pending");
  return t("compliance.detail.missing");
};
const holdKey = (hold: string) => hold === "expired" ? "compliance.products.holdExpired" : hold === "rejected" ? "compliance.products.holdRejected" : "compliance.products.holdMissing";
const inDays = (days: number) => {
  const date = new Date(Date.now() + 3 * 3_600_000);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * Документ о соответствии в карточке модерации (17.10.2026, 31.10.2026): что
 * требует категория и на каком основании, привязанный документ с проверкой
 * («Подтвердить» / «Отклонить»), и ручной допуск «Сертификат на партию
 * подтверждён площадкой» - когда документ показали вне системы.
 */
export function ProductComplianceCard({
  product
}: {
  product: Product;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canModerate = hasAdminPermission(user, "action.products.moderate");
  const [admitting, setAdmitting] = useState(false);
  const [until, setUntil] = useState(inDays(30));
  const [comment, setComment] = useState("");
  const [kind, setKind] = useState<ComplianceDocumentKind | "">("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const c = product.compliance;
  if (!c) return null;
  const doc = c.document;
  const admission = c.admission ?? null;
  const refresh = () => Promise.all([queryClient.invalidateQueries({
    queryKey: productKeys.all
  }), queryClient.invalidateQueries({
    queryKey: complianceSummaryKey
  }), queryClient.invalidateQueries({
    queryKey: complianceJournalKey
  })]);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
      setAdmitting(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const admit = () => {
    if (!comment.trim()) {
      setError(t("compliance.admission.commentRequired"));
      return;
    }
    void run(() => api.compliance.admit(product.id, {
      until,
      comment: comment.trim(),
      ...(number.trim() ? {
        number: number.trim(),
        kind: kind || null
      } : {})
    }));
  };
  const headline = c.state === "not_required" ? t("compliance.detail.notRequired") : c.state === "exempt" ? t("compliance.detail.exempt") : c.state === "ok" ? t("compliance.detail.ok") : c.state === "admitted" ? t("compliance.detail.admitted", {
    date: ruDay(admission?.until)
  }) : complianceBlockText(c, t);
  const tone = c.state === "pending" ? "outline" : c.blocksPublication ? "destructive" : c.state === "ok" || c.state === "admitted" ? "default" : "outline";
  const rule = c.category;
  return <Card data-testid="product-compliance-card" data-state={c.state}>
      <CardHeader>
        <CardTitle>{t("compliance.detail.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Badge variant={tone} className="h-auto whitespace-normal text-left">
          {headline}
        </Badge>
        {rule?.required && <div className="space-y-0.5 text-xs text-muted-foreground" data-testid="product-compliance-rule">
            {rule.kinds.length > 0 && <p>
                {t("compliance.detail.need", {
            kinds: rule.kinds.map(k => t(`compliance.kinds.${k}`)).join(" / ")
          })}
              </p>}
            {rule.basis && <p>{t("compliance.detail.basis", {
            basis: rule.basis
          })}</p>}
            {c.serialBase && <p>{t("compliance.detail.serialBase")}</p>}
            {rule.strict && <p>{t("compliance.detail.strict")}</p>}
          </div>}
        {product.status === "blocked" && c.hold && <p className="text-destructive">
            {t("compliance.detail.hold", {
          reason: t(holdKey(c.hold))
        })}
          </p>}
        {doc && <ComplianceReviewPanel doc={doc} />}

        {c.required && !c.exempt && <div className="space-y-2 border-t pt-3" data-testid="product-compliance-admission">
            <p className="flex items-center gap-1.5 font-medium">
              <ShieldCheck className="size-4" /> {t("compliance.admission.title")}
            </p>
            {admission ? <div className="space-y-1">
                <p className={admission.expired ? "text-destructive" : undefined}>
                  {admission.expired ? t("compliance.admission.expired", {
              date: ruDay(admission.until)
            }) : t("compliance.admission.until", {
              date: ruDay(admission.until)
            })}
                </p>
                <p className="text-muted-foreground">{admission.comment}</p>
                {admission.number && <p className="text-xs text-muted-foreground">
                    {admission.kind ? `${t(`compliance.kinds.${admission.kind}`)} ` : ""}№{" "}
                    {admission.number}
                  </p>}
                <p className="text-xs text-muted-foreground">
                  {t("compliance.admission.by", {
              name: admission.byName ?? "-",
              date: ruDay(admission.at)
            })}
                </p>
              </div> : <p className="text-xs text-muted-foreground">{t("compliance.admission.hint")}</p>}
            {canModerate && !admitting && <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setAdmitting(true)} disabled={busy} data-testid="product-compliance-admit">
                  {admission && !admission.expired ? t("compliance.admission.extend") : t("compliance.admission.grant")}
                </Button>
                {admission && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void run(() => api.compliance.revokeAdmission(product.id))} disabled={busy}>
                    {t("compliance.admission.revoke")}
                  </Button>}
              </div>}
            {canModerate && admitting && <div className="space-y-2 rounded-md border p-3">
                <div className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">
                    {t("compliance.admission.untilLabel")}
                  </span>
                  <Input type="date" value={until} onChange={e_0 => setUntil(e_0.target.value)} className="w-44" data-testid="admission-until" />
                </div>
                <div className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">
                    {t("compliance.admission.commentLabel")}
                  </span>
                  <textarea value={comment} onChange={e_1 => setComment(e_1.target.value)} placeholder={t("compliance.admission.commentPlaceholder")} className="min-h-16 w-full rounded-md border bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-ring" data-testid="admission-comment" />
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={kind} onChange={e_2 => setKind(e_2.target.value as ComplianceDocumentKind | "")} aria-label={t("compliance.detail.kind")}>
                    <option value="">{t("compliance.admission.kindAny")}</option>
                    {COMPLIANCE_DOCUMENT_KINDS.map(k_0 => <option key={k_0} value={k_0}>
                        {t(`compliance.kinds.${k_0}`)}
                      </option>)}
                  </Select>
                  <Input value={number} onChange={e_3 => setNumber(e_3.target.value)} placeholder={t("compliance.admission.numberPlaceholder")} maxLength={120} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("compliance.admission.numberHint")}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={admit} disabled={busy} data-testid="admission-save">
                    {t("compliance.admission.save")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAdmitting(false)} disabled={busy}>
                    {t("confirm.cancel")}
                  </Button>
                </div>
              </div>}
          </div>}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>;
}